import React from 'react';
import moment from 'moment';
import { Box, Typography, Button, Grid2 as Grid, ChipOwnProps } from '@mui/material';
import { LinkField } from 'framework/ui/field';
import { Badge, Tooltip } from 'framework/ui/mui-builder';
import { DATE_FORMAT } from 'framework/utils';
import LeaveTypeField from 'domain/people/leave/shared/field/LeaveTypeField';
import LeaveContext from 'domain/people/leave/LeaveContext';
import { useLeaveUrl } from 'domain/people/leave/leaveRouting';
import RejectLeaveIconButton from '../../shared/buttons/RejectLeaveIconButton';
import { LeaveDto } from 'domain/shared/leave';
import { LEAVE_STATUSES, LEAVE_STATUSES_ALIASES } from 'domain/shared/leave';
import { useListContext } from 'framework/list';

const STATUS_COLORS: Record<string, ChipOwnProps['color']> = {
  [LEAVE_STATUSES_ALIASES.REGISTERED]: 'success',
  [LEAVE_STATUSES_ALIASES.IN_APPROVAL]: 'default',
  [LEAVE_STATUSES_ALIASES.APPROVED]: 'primary',
  [LEAVE_STATUSES_ALIASES.REJECTED]: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  [LEAVE_STATUSES_ALIASES.REGISTERED]: LEAVE_STATUSES.REGISTERED,
  [LEAVE_STATUSES_ALIASES.IN_APPROVAL]: LEAVE_STATUSES.IN_APPROVAL,
  [LEAVE_STATUSES_ALIASES.APPROVED]: LEAVE_STATUSES.APPROVED,
  [LEAVE_STATUSES_ALIASES.REJECTED]: LEAVE_STATUSES.REJECTED,
};

const formatDateRange = (start: string, end: string) => {
  const startDate = moment(start).format(DATE_FORMAT.DAY);
  const endDate = moment(end).format(DATE_FORMAT.DAY);

  return startDate === endDate ? startDate : `${startDate} – ${endDate}`;
};

const UpcomingLeaveItem = ({ leave }: { leave: LeaveDto }) => {
  const { getSingleEntityUrl } = useLeaveUrl();

  return (
    <Grid size={12}>
      <Grid container alignItems="center">
        <Grid size={4}>
          <Typography variant="subtitle1">
            {formatDateRange(leave.period.startDate, leave.period.actualEndDate || leave.period.plannedEndDate)}
          </Typography>
        </Grid>

        <Grid size={4}>
          <LinkField
            isInternal
            value={getSingleEntityUrl(leave.id)}
            linkText={<LeaveTypeField value={leave.type} subType={leave.subType} sickDayReason={leave.sickDayReason} />}
          />
        </Grid>

        <Grid size={3}>
          <Tooltip title={leave.status === LEAVE_STATUSES_ALIASES.APPROVED ? STATUS_LABELS[leave.status] : undefined}>
            <Badge sx={{ maxWidth: 100 }} color={STATUS_COLORS[leave.status] ?? 'default'}>
              <Box
                sx={{
                  width: 70,
                  maxWidth: 70,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}
              >
                {STATUS_LABELS[leave.status] ?? leave.status}
              </Box>
            </Badge>
          </Tooltip>
        </Grid>

        <Grid display="flex" justifyContent="flex-end" size={1}>
          <RejectLeaveIconButton leave={leave} isVisible={leave.status !== LEAVE_STATUSES_ALIASES.REJECTED} />
        </Grid>
      </Grid>
    </Grid>
  );
};

const UpcomingLeavesLayout: React.FC = () => {
  const { data: { items: leaves = [] } = {}, hasMoreData, loadNextPage, isLoadingNextPage } = useListContext();

  return (
    <Box>
      <Typography variant="h6" color="primary" mb={2}>
        Upcoming
      </Typography>

      {!leaves || leaves.length === 0 ? (
        <Typography variant="body1" color="textSecondary">
          No upcoming leaves
        </Typography>
      ) : (
        <>
          <Grid container gap={1}>
            {leaves.map((leave: LeaveDto) => (
              <LeaveContext key={leave.id} id={leave.id}>
                <UpcomingLeaveItem leave={leave} />
              </LeaveContext>
            ))}
          </Grid>

          {(hasMoreData?.() || isLoadingNextPage) && (
            <Button
              variant="text"
              onClick={loadNextPage}
              disabled={isLoadingNextPage}
              sx={{
                mt: 2,
                alignSelf: 'center',
              }}
              size="small"
            >
              {isLoadingNextPage ? 'Loading...' : 'Load more'}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default UpcomingLeavesLayout;
